import {
  Refresh as RefreshIcon,
} from '@mui/icons-material/';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ReadingSchema, SensorModelSchema } from '../../../../requests/api/apiTypes';
import { fetchReadingsFromAPI } from '../../../../services/readingsService';
import { ReduxState } from '../../../../types';
import useStyles from '../styles';
import { processWeatherReadingValue, WeatherReportData } from '../utils';
import { WeatherDataItemProps, weatherDataConfig } from './weatherConstants';
import apiClient from '../../../../requests/api/apiClient';

interface WeatherReportProps {
  vertical?: boolean;
  hideStationSelectorAndUpdate?: boolean;
}

const WeatherDataItem: React.FC<WeatherDataItemProps> = (
  {
    icon,
    label,
    value,
    unit,
  }: WeatherDataItemProps,
) => (
  <Box display="flex" alignItems="center">
    {icon}
    <Typography variant="body1" marginLeft={1}>
      {label}: <strong>{value !== null ? `${Number(value).toFixed(2)}${unit || ''}` : 'No disponible'}</strong>
    </Typography>
  </Box>
);

const WeatherReport = (
  { vertical = false, hideStationSelectorAndUpdate = false }: WeatherReportProps,
) => {
  const classes = useStyles();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const [weatherData, setWeatherData] = useState<WeatherReportData | null>(null);
  const [weatherStations, setWeatherStations] = useState<any>([]);

  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedWeatherStation, setSelectedWeatherStation] = useState<string>('');
  const [selectedWeatherStationTelemetryType, setSelectedWeatherStationTelemetryType] = useState<string>('wiseconn');
  const { enqueueSnackbar } = useSnackbar();
  const currentAccountId = useRef<string | null>(null);

  useEffect(() => {
    const getSensors = async () => {
      if (selectedFarm?.id) {
        try {
          const response = await apiClient.sensorModels.sensorModelsByFarmId(selectedFarm?.id);
          const weatherStationsResponse = response?.data?.sensorModels?.filter(
            (sensorModel: SensorModelSchema) => sensorModel.isWeatherStation === true,
          );
          setWeatherStations(weatherStationsResponse ?? []);
        } catch {
          setWeatherStations([]);
        }
      }
    };
    getSensors();
  }, []);

  useEffect(() => {
    const clearWeatherData = async () => {
      setWeatherData(null);
      setLastUpdated(null);
      setSelectedWeatherStation('');
      setSelectedWeatherStationTelemetryType('wiseconn');
    };

    if (selectedAccount?.id !== currentAccountId.current) {
      currentAccountId.current = selectedAccount?.id || null;
      clearWeatherData();
    }
  }, [selectedAccount?.id]);

  useEffect(() => {
    const currentStationExists = weatherStations.some(
      (station: any) => station.zoneId === selectedWeatherStation,
    );

    if (!currentStationExists && selectedWeatherStation !== '') {
      setSelectedWeatherStation('');
      setSelectedWeatherStationTelemetryType('wiseconn');
    }

    if (selectedSector?.weatherZoneId) {
      setSelectedWeatherStation(selectedSector.weatherZoneId);
      // Find the weather station device to get its telemetryType
      const weatherStation = weatherStations.find(
        (station: any) => station.zoneId === selectedSector.weatherZoneId,
      );
      const telemetryType = weatherStation?.telemetryType || 'wiseconn';
      setSelectedWeatherStationTelemetryType(telemetryType);
    } else if (weatherStations.length > 0 && selectedWeatherStation === '') {
      const firstStation = weatherStations[0];
      setSelectedWeatherStation(firstStation.zoneId || '');
      setSelectedWeatherStationTelemetryType(firstStation.telemetryType || 'wiseconn');
    } else if (weatherStations.length === 0) {
      setSelectedWeatherStation('');
      setSelectedWeatherStationTelemetryType('wiseconn');
    }
  }, [selectedSector, weatherStations, selectedFarm?.id, selectedWeatherStation]);

  const getInitialWeatherData = (): WeatherReportData => ({
    windSpeed: null,
    windDirection: null,
    solarRadiation: null,
    humidity: null,
    temperature: null,
    maxTemperature: null,
    minTemperature: null,
    dailyRain: null,
    hourlyRain: null,
    eto: null,
  });

  const processReadings = (readings: ReadingSchema[]): WeatherReportData => {
    const processedData = getInitialWeatherData();
    readings.forEach((reading) => processWeatherReadingValue(reading, processedData));

    return processedData;
  };

  const fetchWeatherData = async () => {
    try {
      if (!selectedFarm?.id) {
        return;
      }

      setLoading(true);
      const now = new Date();
      const startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const endDate = now;

      let readings: ReadingSchema[] = [];

      if (selectedWeatherStation) {
        readings = await fetchReadingsFromAPI({
          granularity: 'all',
          farmId: selectedFarm.id,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          wiseconnZoneId: selectedWeatherStation,
          sn: selectedWeatherStationTelemetryType === 'sn' ? selectedWeatherStation : '',
          telemetryType: selectedWeatherStationTelemetryType,
        });
      }

      const processedData = processReadings(readings);
      const lastUpdateTime = new Date().toLocaleString();

      setLastUpdated(lastUpdateTime);
      setWeatherData(processedData);
    } catch (error) {
      enqueueSnackbar('Error al actualizar el reporte del clima', { variant: 'error' });
      setLastUpdated('Error - Intenta nuevamente');
      setWeatherData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWeatherStation) {
      fetchWeatherData();
    }
  }, [selectedWeatherStation, selectedFarm?.id]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (weatherStations.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={3}>
          <Typography variant="h6" color="textSecondary">
            No hay estaciones de clima disponibles
          </Typography>
        </Box>
      );
    }

    const hasSelectedSector = Boolean(selectedSector && Object.keys(selectedSector).length > 0);

    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" color="primary">
            Clima actual
          </Typography>

          {!hideStationSelectorAndUpdate && (
            <Box display="flex" alignItems="center">
              <Box sx={{ minWidth: 200, mr: 2 }}>
                <TextField
                  label="Estación de clima"
                  value={selectedWeatherStation}
                  onChange={(event) => {
                    const newStationZoneId = event.target.value;
                    setSelectedWeatherStation(newStationZoneId);
                    // Find the weather station device to get its telemetryType
                    const weatherStation = weatherStations.find(
                      (station: any) => station.zoneId === newStationZoneId,
                    );
                    setSelectedWeatherStationTelemetryType(weatherStation?.telemetryType || 'wiseconn');
                  }}
                  fullWidth
                  select
                  variant="standard"
                  disabled={hasSelectedSector}
                >
                  {weatherStations.map((station: SensorModelSchema) => (
                    <MenuItem key={station.id} value={station.zoneId}>
                      {`Modelo: ${station.name ?? 'N/A'} - ${station.zoneId}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="flex-end" marginRight={1}>
                <Typography variant="body2" className={classes.lastUpdatedText}>
                  Última actualización:
                </Typography>
                <Typography variant="body2" className={classes.lastUpdatedDate}>
                  {lastUpdated || 'N/A'}
                </Typography>
              </Box>
              <IconButton onClick={fetchWeatherData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        <Divider className={classes.divider} />

        <Grid
          container
          spacing={vertical ? 0 : 2}
          marginTop={1}
          direction={vertical ? 'column' : 'row'}
        >
          {vertical ? (
            weatherDataConfig.map((config) => (
              <Grid item xs={12} key={config.key}>
                <WeatherDataItem
                  icon={config.icon}
                  label={config.label}
                  value={selectedWeatherStation
                    ? (weatherData?.
                      [config.key as keyof WeatherReportData] as number | null) : null}
                  unit={config.unit}
                />
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={4}>
                {weatherDataConfig.slice(0, 4).map((config) => (
                  <Grid item md={12} key={config.key}>
                    <WeatherDataItem
                      icon={config.icon}
                      label={config.label}
                      value={selectedWeatherStation
                        ? (weatherData?.
                          [config.key as keyof WeatherReportData] as number | null) : null}
                      unit={config.unit}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {weatherDataConfig.slice(4, 7).map((config) => (
                  <Grid item md={12} key={config.key}>
                    <WeatherDataItem
                      icon={config.icon}
                      label={config.label}
                      value={selectedWeatherStation
                        ? (weatherData?.
                          [config.key as keyof WeatherReportData] as number | null) : null}
                      unit={config.unit}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {weatherDataConfig.slice(7).map((config) => (
                  <Grid item md={12} key={config.key}>
                    <WeatherDataItem
                      icon={config.icon}
                      label={config.label}
                      value={selectedWeatherStation
                        ? (weatherData?.
                          [config.key as keyof WeatherReportData] as number | null) : null}
                      unit={config.unit}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Card>
      <CardContent>
        {!selectedFarm ? (
          <>
            <Typography
              variant='h3'
              color="primary"
              gutterBottom
              display="inline"
            >
              Clima actual
            </Typography>
            <Divider className={classes.divider} />
            <Typography className={classes.noFarmSelectedText}>
              Elige un campo para ver el reporte del clima
            </Typography>
          </>
        ) : (
          renderContent()
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherReport;
